/**
 * Auth Store (Zustand)
 * 
 * Global authentication state management
 * - User state and tokens
 * - Authentication status
 * - Role checks (isAdmin, isCoach)
 * - Login, logout, and token refresh actions
 * 
 * Requirements: 1.9, 1.10, 1.11
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  getStoredTokens,
  storeTokens,
  clearTokens,
  redirectToLogout,
  refreshTokens as refreshCognitoTokens,
  getUserGroups,
  areTokensExpired,
} from '../auth/cognito';
import { restClient, User } from '../api/rest-client';

// Types
interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface AuthState {
  // State
  user: User | null;
  tokens: CognitoTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Computed properties
  isAdmin: boolean;
  isCoach: boolean;
  groups: string[];

  // Rider association flag — set by Dashboard after fetching user's riders
  // null = not yet determined (dashboard hasn't loaded), true/false = known
  hasRiders: boolean | null;

  // Actions
  login: (tokens: CognitoTokens) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  fetchUser: () => Promise<void>;
  clearError: () => void;
  initialize: () => Promise<void>;
  setHasRiders: (value: boolean) => void;
}

/**
 * Auth Store
 * 
 * Manages authentication state globally using Zustand
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isAdmin: false,
        isCoach: false,
        groups: [],
        hasRiders: null,

        /**
         * Login with Cognito tokens
         * Requirements: 1.9, 1.10, 1.11
         */
        login: async (tokens: CognitoTokens) => {
          set({ isLoading: true, error: null });

          try {
            // Store tokens
            storeTokens(tokens);

            // Extract user groups from ID token
            const groups = getUserGroups(tokens.idToken);
            const isAdmin = groups.includes('admin');
            const isCoach = groups.includes('coach');

            // Fetch user details from API
            const user = await restClient.getCurrentUser();

            set({
              tokens,
              user,
              groups,
              isAdmin,
              isCoach,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            console.error('Login failed:', error);
            set({
              error: error instanceof Error ? error.message : 'Login failed',
              isLoading: false,
            });
            throw error;
          }
        },

        /**
         * Logout and clear all auth state
         * Requirements: 1.7
         */
        logout: () => {
          // Clear tokens from storage
          clearTokens();

          // Reset state
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isAdmin: false,
            isCoach: false,
            groups: [],
            hasRiders: null,
            error: null,
          });

          // Redirect to Cognito logout
          redirectToLogout();
        },

        /**
         * Refresh expired token
         * Requirements: 1.5, 1.6
         */
        refreshToken: async (): Promise<boolean> => {
          const { tokens } = get();

          if (!tokens) {
            return false;
          }

          // Check if token is expired
          if (!areTokensExpired(tokens)) {
            return true; // Token still valid
          }

          try {
            const newTokens = await refreshCognitoTokens(tokens);

            if (newTokens) {
              storeTokens(newTokens);

              // Extract updated groups
              const groups = getUserGroups(newTokens.idToken);
              const isAdmin = groups.includes('admin');
              const isCoach = groups.includes('coach');

              set({
                tokens: newTokens,
                groups,
                isAdmin,
                isCoach,
              });

              return true;
            }

            // Refresh failed - logout
            get().logout();
            return false;
          } catch (error) {
            console.error('Token refresh failed:', error);
            get().logout();
            return false;
          }
        },

        /**
         * Fetch current user details from API
         */
        fetchUser: async () => {
          set({ isLoading: true, error: null });

          try {
            const user = await restClient.getCurrentUser();
            set({ user, isLoading: false });
          } catch (error) {
            console.error('Failed to fetch user:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch user',
              isLoading: false,
            });
          }
        },

        /**
         * Clear error state
         */
        clearError: () => {
          set({ error: null });
        },

        /**
         * Set rider association flag
         * Called by Dashboard after fetching user's riders
         */
        setHasRiders: (value: boolean) => {
          set({ hasRiders: value });
        },

        /**
         * Initialize auth state from stored tokens
         * Called on app startup
         */
        initialize: async () => {
          const storedTokens = getStoredTokens();

          if (!storedTokens) {
            set({ isAuthenticated: false });
            return;
          }

          // Check if tokens are expired
          if (areTokensExpired(storedTokens)) {
            // Try to refresh
            const refreshed = await get().refreshToken();
            if (!refreshed) {
              return;
            }
          }

          // Extract groups from token
          const groups = getUserGroups(storedTokens.idToken);
          const isAdmin = groups.includes('admin');
          const isCoach = groups.includes('coach');

          set({
            tokens: storedTokens,
            groups,
            isAdmin,
            isCoach,
            isAuthenticated: true,
          });

          // Fetch user details
          try {
            const user = await restClient.getCurrentUser();
            set({ user });
          } catch (error) {
            console.error('Failed to fetch user on init:', error);
            // Don't logout on fetch failure - user might be offline
          }
        },
      }),
      {
        name: 'auth-storage',
        // Only persist tokens, not the entire state
        partialize: (state) => ({
          tokens: state.tokens,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

// Selectors for convenience
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsAdmin = (state: AuthState) => state.isAdmin;
export const selectIsCoach = (state: AuthState) => state.isCoach;
export const selectUser = (state: AuthState) => state.user;
export const selectGroups = (state: AuthState) => state.groups;
export const selectHasRiders = (state: AuthState) => state.hasRiders;

export default useAuthStore;
